"use client";

import { useMediaQuery } from "@/app/directories/companion/(main)/roster/hooks/useMediaQuery";
import { useKioskEvent } from "@/app/directories/kiosk/contexts/KioskEventContext";
import { getBuyNowDestinationPath } from "@/app/directories/shop/lib/storefrontLogicHelper";
import { ProductType } from "@/lib/graphql/generated-gql";
import { getAllEnabledOrgConfigFlagsByOrgId } from "@frontend/lib/actions/actions";
import type { GetProductsByCategoryIdQuery } from "@frontend/lib/graphql/generated-gql";
import { cn } from "@frontend/shared/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { cva } from "class-variance-authority";
import { useKeenSlider } from "keen-slider/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import React, { useState, useEffect } from "react";
import { ProductOptionsContainer } from "../featuredCarousel/ProductOptionsContainer";
import { ProductCardCarouselSlide } from "./ProductCardCarouselSlide";
import { ProductCardCarouselSlideKiosk } from "./ProductCardCarouselSlideKiosk";

const navigationButtonVariants = cva(
  "w-12 h-12 rounded-full flex items-center justify-center transition-opacity opacity-100 cursor-pointer",
  {
    variants: {
      hasSecondaryBackground: {
        true: "", // No border when secondary background color exists
        false: "bg-white border border-black", // White bg with black border when no secondary color
      },
    },
    defaultVariants: {
      hasSecondaryBackground: false,
    },
  },
);

export const ProductCardCarousel = ({
  products,
  clientSlug,
  categoryName,
  showIcon = false,
  iconURL = "",
  primaryColor,
  primaryBackgroundColor,
  secondaryBackgroundColor,
  orgId,
  isMinimal = false,
  kioskMode = false,
}: {
  products: GetProductsByCategoryIdQuery["findManyProducts"];
  clientSlug: string;
  categoryName: string;
  showIcon?: boolean;
  iconURL?: string;
  primaryColor?: string | null;
  primaryBackgroundColor?: string | null;
  secondaryBackgroundColor?: string | null;
  orgId: number;
  isMinimal?: boolean;
  kioskMode?: boolean;
}) => {
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const [loaded, setLoaded] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    null,
  );
  const router = useRouter();
  const { send: sendKioskEvent } = useKioskEvent();
  const { data: configFlags, isPending: isConfigFlagsPending } = useQuery({
    queryKey: ["configFlags", orgId],
    queryFn: () => getAllEnabledOrgConfigFlagsByOrgId(orgId),
    enabled: !!orgId,
  });

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    initial: 0,
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
    },
    slides: {
      spacing: 24,
      origin: "auto",
    },
    breakpoints: {
      "(min-width: 320px)": {
        slides: {
          perView: 1,
          spacing: 24,
        },
      },
      "(min-width: 768px)": {
        slides: {
          perView: "auto",
          spacing: 24,
        },
      },
    },
    created() {
      setLoaded(true);
    },
  });

  const canScrollPrev = currentSlide > 0;
  const canScrollNext =
    loaded &&
    instanceRef.current &&
    currentSlide < instanceRef.current.track.details.maxIdx;

  const hasCustomSecondaryColor =
    secondaryBackgroundColor && secondaryBackgroundColor !== "#ffffff";

  const isMobile = useMediaQuery("(max-width: 768px)");

  // Update slider on window resize
  useEffect(() => {
    const handleResize = () => {
      if (instanceRef.current) {
        instanceRef.current.update();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [instanceRef]);

  const handleBuyNowClick = (
    product: GetProductsByCategoryIdQuery["findManyProducts"][number],
  ) => {
    const hrt = product.productToHrt[0].hrt;
    const hrtId = hrt.hrtId;

    if (kioskMode) {
      sendKioskEvent({
        type: "SUBMIT",
        data: { hrtIds: [hrtId] },
      });
      return;
    }

    if (product.productToHrt.length > 1) {
      setSelectedProductId(product.id);
      return;
    }

    // configFlags drives the schedule-vs-review routing decision; bail until
    // the query settles so an eager click can't silently land on /review.
    if (isConfigFlagsPending) return;
    router.push(
      getBuyNowDestinationPath({
        hrt,
        orgId,
        configFlags,
        clientSlug,
        hrtId,
      }),
    );
  };

  return (
    <div
      style={{
        padding:
          !kioskMode && isMobile
            ? "32px 16px 0px 16px"
            : !kioskMode && !isMobile
              ? "48px 48px 0px 48px"
              : "0px",
      }}
      className="relative w-full mx-auto overflow-visible"
    >
      <div
        className={cn(
          "flex flex-col gap-0 lg:gap-4 mb-6 pb-6 overflow-visible",
          kioskMode && "gap-0",
        )}
      >
        <div className={"flex flex-row justify-between items-center pr-0"}>
          <div className="flex flex-row justify-center items-center gap-4">
            {showIcon && iconURL && (
              <Image
                src={iconURL}
                alt={categoryName}
                width={500}
                height={500}
                className="w-8 h-8 lg:w-12 lg:h-12 object-contain"
              />
            )}
            <h2
              className={cn(
                "config-h4 text-xl lg:text-3xl",
                kioskMode && "kiosk-h1 !text-3xl",
              )}
            >
              {categoryName}
            </h2>
          </div>
          {loaded && instanceRef.current && (
            <div className="flex justify-end gap-2">
              {!isMobile && (
                <button
                  type="button"
                  onClick={() => {
                    instanceRef.current?.prev();
                  }}
                  disabled={!canScrollPrev}
                  className={cn(
                    !canScrollPrev && "invisible",
                    navigationButtonVariants({
                      hasSecondaryBackground: !!hasCustomSecondaryColor,
                    }),
                  )}
                  style={{
                    backgroundColor: hasCustomSecondaryColor
                      ? secondaryBackgroundColor
                      : undefined,
                  }}
                >
                  <ChevronLeft className="w-7 h-7" />
                </button>
              )}
              {!isMobile && (
                <button
                  type="button"
                  onClick={() => {
                    instanceRef.current?.next();
                  }}
                  disabled={!canScrollNext}
                  className={cn(
                    !canScrollNext && "invisible",
                    navigationButtonVariants({
                      hasSecondaryBackground: !!hasCustomSecondaryColor,
                    }),
                  )}
                  style={{
                    backgroundColor: hasCustomSecondaryColor
                      ? secondaryBackgroundColor
                      : undefined,
                  }}
                >
                  <ChevronRight className="w-7 h-7" />
                </button>
              )}
            </div>
          )}
        </div>
        <div ref={sliderRef} className="keen-slider py-4">
          {products.map((product) => {
            return kioskMode ? (
              <ProductCardCarouselSlideKiosk
                key={`product-card-kiosk-${product.id}`}
                product={product}
                clientSlug={clientSlug}
                orgId={orgId}
                onBuyNowClick={() => handleBuyNowClick(product)}
              />
            ) : (
              <ProductCardCarouselSlide
                key={`product-card-${product.id}`}
                product={product}
                clientSlug={clientSlug}
                showBuyNowAndViewDetails={
                  product.product_type === ProductType.PdpAndBuyNow
                }
                primaryColor={primaryColor}
                orgId={orgId}
                isMinimal={isMinimal}
                onBuyNowClick={() => handleBuyNowClick(product)}
              />
            );
          })}
        </div>
      </div>
      {selectedProductId && (
        <ProductOptionsContainer
          product={
            products.find((p) => p.id === selectedProductId) ?? products[0]
          }
          clientSlug={clientSlug}
          orgId={orgId}
          kioskMode={kioskMode}
          open={selectedProductId !== null}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedProductId(null);
            }
          }}
        />
      )}
    </div>
  );
};
